import { createPortal } from "react-dom";
import { useThemeContext } from "../theme/ThemeContext";
import { Button } from "./shared";

export default function FilterBar({
  // Sort
  sortBy, setSortBy,
  sortDir, setSortDir,
  sortMenuRect, setSortMenuRect,
  isWide,
  // Filters
  filterInAirline,  setFilterInAirline,
  filterOutAirline, setFilterOutAirline,
  filterOrigin,     setFilterOrigin,
  filterDest,       setFilterDest,
  filterStatus,     setFilterStatus,
  filterOverdue,    setFilterOverdue,
  filterHideP2P,
  searchQuery, setSearchQuery,
  // Actions
  isUA,
  sorted,
  confirmDeleteAll, setConfirmDeleteAll,
  onOpenFilters,
  onClearFilters,
  onDeleteAll,
}) {
  const { SA, isDark, TYPOGRAPHY, RADIUS, tokens } = useThemeContext();

  const activeFilterCount = [
    filterInAirline  !== "ALL",
    filterOutAirline !== "ALL",
    filterOrigin     !== "ALL",
    filterDest       !== "ALL",
    filterStatus     !== "ALL",
    filterOverdue,
    filterHideP2P,
  ].filter(Boolean).length;

  const selectStyle = {
    background: SA.darkGray, border: `1px solid ${SA.borderGray}`, borderRadius: RADIUS.sm,
    color: SA.white, padding: "6px 22px 6px 8px", fontSize: TYPOGRAPHY.caption, cursor: "pointer",
    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", outline: "none",
    appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center",
    colorScheme: "dark",
  };

  const labelStyle = {
    fontSize: TYPOGRAPHY.tiny, color: SA.borderGray, letterSpacing: "0.14em",
    textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", whiteSpace: "nowrap",
  };

  const SORT_OPTS = [
    { value: "std",    label: isWide ? "Departure Time (Default)" : "Dep Time" },
    { value: "sta",    label: isWide ? "Arrival Time" : "Arr Time" },
    { value: "conn",   label: "Conn Time" },
    { value: "status", label: "Status" },
  ];
  const selectedSort = SORT_OPTS.find(o => o.value === sortBy) || SORT_OPTS[0];

  return (
    <div style={{
      borderBottom: `1px solid ${SA.borderGray}`,
      background: isDark ? "#0D1018" : "#E8EBF2",
    }}>
      <div style={{
        maxWidth: 1400, margin: "0 auto", padding: "8px 10px",
        display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap",
        overflowX: "auto", WebkitOverflowScrolling: "touch",
        maskImage: "linear-gradient(to right, transparent 0, black 16px, black calc(100% - 16px), transparent 100%)",
        WebkitMaskImage: "linear-gradient(to right, transparent 0, black 16px, black calc(100% - 16px), transparent 100%)",
      }}>

        {/* Sort */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <div style={{ position: "relative" }}>
            <div
              onClick={e => {
                const r = e.currentTarget.getBoundingClientRect();
                setSortMenuRect(prev => prev ? null : r);
              }}
              style={{ ...selectStyle, display: "flex", alignItems: "center", userSelect: "none" }}
            >
              {selectedSort.label}
            </div>
            {sortMenuRect && createPortal(
              <>
                <div onClick={() => setSortMenuRect(null)} style={{ position: "fixed", inset: 0, zIndex: 199 }} />
                <div style={{
                  position: "fixed", top: sortMenuRect.bottom + 4, left: sortMenuRect.left, zIndex: 200,
                  background: SA.darkGray, border: `1px solid ${SA.borderGray}`, borderRadius: RADIUS.sm,
                  minWidth: sortMenuRect.width, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                }}>
                  {SORT_OPTS.map(o => (
                    <div
                      key={o.value}
                      onClick={() => { setSortBy(o.value); setSortDir("asc"); setSortMenuRect(null); }}
                      style={{
                        padding: "8px 12px", fontSize: TYPOGRAPHY.caption,
                        color: o.value === sortBy ? SA.white : SA.silver,
                        background: o.value === sortBy ? SA.borderGray : "transparent",
                        cursor: "pointer", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", whiteSpace: "nowrap",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = SA.borderGray}
                      onMouseLeave={e => e.currentTarget.style.background = o.value === sortBy ? SA.borderGray : "transparent"}
                    >
                      {o.label}
                    </div>
                  ))}
                </div>
              </>,
              document.body
            )}
          </div>
          <Button
            variant="icon"
            onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
            style={{
              width: 28, height: 28, borderRadius: RADIUS.sm,
              background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
              color: SA.white, fontSize: TYPOGRAPHY.note, flexShrink: 0,
            }}
          >
            {sortDir === "asc" ? "↑" : "↓"}
          </Button>
        </div>

        {/* Filter button */}
        <Button
          onClick={onOpenFilters}
          style={{
            gap: 5, padding: "6px 10px",
            background: activeFilterCount > 0 ? "rgba(255,255,255,0.07)" : "transparent",
            border: `1px solid ${activeFilterCount > 0 ? SA.silver : SA.borderGray}`,
            borderRadius: RADIUS.sm, color: activeFilterCount > 0 ? SA.white : SA.silver,
            fontSize: TYPOGRAPHY.caption, fontFamily: "'Barlow',sans-serif", transition: "all 0.15s", flexShrink: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1.5 3.5h11M3.5 7h7M5.5 10.5h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          {activeFilterCount > 0 && (
            <span style={{
              background: SA.accent, color: "#fff", borderRadius: RADIUS.md,
              fontSize: TYPOGRAPHY.tiny, fontWeight: 700, padding: "1px 6px",
              minWidth: 16, textAlign: "center",
            }}>
              {activeFilterCount}
            </span>
          )}
        </Button>

        {/* Clear filters */}
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            onClick={onClearFilters}
            style={{
              gap: 4, padding: "5px 10px",
              border: `1px solid ${SA.borderGray}`, borderRadius: RADIUS.sm,
              color: SA.silver, fontSize: TYPOGRAPHY.label,
              fontFamily: "'Barlow',sans-serif", flexShrink: 0,
            }}
          >
            <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
              <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </Button>
        )}

        {/* Search: flight number, airport or gate */}
        <div style={{ position: "relative", display: "flex", alignItems: "center", flexShrink: 0 }}>
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true"
            style={{ position: "absolute", left: 8, color: SA.silver, pointerEvents: "none" }}>
            <circle cx="6" cy="6" r="4.25" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M9.2 9.2L12.5 12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          <input
            type="search" value={searchQuery} aria-label="Search flight, airport or gate"
            placeholder={isWide ? "Flight, airport or gate" : "Search"}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Escape") { setSearchQuery(""); e.currentTarget.blur(); } }}
            autoCorrect="off" autoCapitalize="characters" spellCheck={false}
            style={{
              width: isWide ? 190 : 110, padding: "6px 24px 6px 26px",
              background: searchQuery ? "rgba(255,255,255,0.07)" : SA.darkGray,
              border: `1px solid ${searchQuery ? SA.silver : SA.borderGray}`, borderRadius: RADIUS.sm,
              color: SA.white, fontSize: TYPOGRAPHY.caption, outline: "none",
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
              WebkitAppearance: "none", appearance: "none",
            }}
          />
          {searchQuery && (
            <Button variant="icon" aria-label="Clear search" onClick={() => setSearchQuery("")}
              style={{ position: "absolute", right: 4, width: 18, height: 18, color: SA.silver, background: "transparent" }}>
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </Button>
          )}
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Result count */}
        {!confirmDeleteAll && (
          <span style={labelStyle}>{sorted.length}</span>
        )}

        {/* Delete All — UA SCCO only */}
        {isUA && sorted.length > 0 && !confirmDeleteAll && (
          <Button
            variant="ghost"
            onClick={() => setConfirmDeleteAll(true)}
            style={{
              gap: 5, padding: "5px 10px",
              border: `1px solid ${SA.borderGray}`, borderRadius: RADIUS.sm,
              color: SA.silver, fontSize: TYPOGRAPHY.label,
              fontFamily: "'Barlow',sans-serif", flexShrink: 0,
            }}
          >
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path d="M2 3h8M5 3V2h2v1M4.5 3v6.5h3V3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Button>
        )}

        {/* Confirm Delete All */}
        {isUA && confirmDeleteAll && (
          <>
            <span style={{ fontSize: TYPOGRAPHY.label, color: tokens.danger, fontFamily: "'Barlow',sans-serif", flexShrink: 0 }}>
              Delete {sorted.length} card{sorted.length !== 1 ? "s" : ""}?
            </span>
            <Button
              onClick={() => { setConfirmDeleteAll(false); onDeleteAll(sorted.map(e => e.id)); }}
              style={{
                padding: "5px 14px", background: tokens.dangerSurface2,
                border: `1px solid ${tokens.dangerBorder}`, borderRadius: RADIUS.sm,
                color: tokens.dangerText2, fontSize: TYPOGRAPHY.label, fontWeight: 700,
                fontFamily: "'Barlow',sans-serif", flexShrink: 0,
              }}
            >
              Confirm
            </Button>
            <Button
              variant="secondary"
              onClick={() => setConfirmDeleteAll(false)}
              style={{
                padding: "5px 10px",
                border: `1px solid ${SA.borderGray}`, borderRadius: RADIUS.sm,
                color: SA.silver, fontSize: TYPOGRAPHY.label,
                fontFamily: "'Barlow',sans-serif", flexShrink: 0,
              }}
            >
              Cancel
            </Button>
          </>
        )}

      </div>
    </div>
  );
}
